import {
  memo,
  useEffect,
  useRef,
  useCallback,
  Ref,
  MutableRefObject,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import isEqual from 'lodash.isequal';
import L, { LatLngBoundsLiteral } from 'leaflet';
import "leaflet-polylinedecorator";
import { TileLayer, GeoJSON, ScaleControl, useMapEvent } from 'react-leaflet';
import { actions } from 'store/GuideUse/actions';
import {
  getCurrentLayerAttributionSelector,
  getCurrentLayerLinkSelector,
  getIsWaypointSelectedSelector,
  getSelectedWaypointSelector,
} from 'store/GuideUse/selectors';
import { LocalStorageManager } from 'services/LocalStorage/LocalStorageManager';
import { DEFAULT_LAYER } from 'views/GuideUse/LayersModal/layers';

import {
  S,
  MarkerClass,
  ClusterClass,
  CustomMarkerClass,
  getClusterInlineStyles,
} from './GuideUseMap.styles';
import {
  GuideUseMapViewProps,
  MarkerIconProps,
} from 'containers/GuideUseMap/GuideUseMap.interfaces';
import 'react-leaflet-markercluster/dist/styles.min.css';
import { useDefaultStateForMapInstance } from '../../hooks/useDefaultStateForMapInstance';
import { getWaypointIcon } from 'utils/waypointHelper';

const PLANNED_ROUTE_COLOR = '#EFCC00';
const PLANNED_ROUTE_ARROW_COLOR = 'gray';

const MarkerIcon = ({ svgFilePath, color, width, height }: MarkerIconProps) => {
  return (
    <S.MarkerContainer
      src={svgFilePath}
      color={color}
      width={width || 30}
      height={height || 40}
    />
  );
};

const GuideUseMapComponent: React.FC<GuideUseMapViewProps> = ({
  map,
  handleSetMap,
  isMobile,
  isEditorMode,
  trailsData,
  waypointsData,
  plannedRoute,
  plannedRouteMarkersData,
  useTrailPopup = true,
  handleAddPlannedRoutePin
}) => {
  const tileLayerRef: Ref<L.TileLayer> = useRef(null);
  const allMarkersRef: MutableRefObject<L.Marker[]> = useRef([]);
  const plannedRoutePolylineRef = useRef<L.Layer | null>(null);
  const plannedRouteDecoratorRef = useRef<L.Layer | null>(null);
  const plannedRouteFocusOnMap = useRef<Boolean>(false);
  const initialOpening = useRef<Boolean>(true);
  let mapPadding = isMobile ? 0 : 400;

  const dispatch = useDispatch();
  const currentLayerLink = useSelector(getCurrentLayerLinkSelector);
  const currentLayerAttribution = useSelector(getCurrentLayerAttributionSelector);
  const selectedWaypoint = useSelector(getSelectedWaypointSelector);
  const isWaypointSelected = useSelector(getIsWaypointSelectedSelector);
  const [localSelectedWaypointId, setLocalSelectedWaypointId] = useState('');
  const [localSelectedPlannedRouteId, setLocalSelectedPlannedRouteId] = useState('');

  useEffect(() => {
    if (map == null){
      return;
    }

    cleanupPlannedRoute();

    if (plannedRoute == null || plannedRoute === undefined || plannedRoute.trail == null || plannedRouteMarkersData == null){
      return
    }

    if (!plannedRoute.isNew && (localSelectedPlannedRouteId === '' || localSelectedPlannedRouteId !== plannedRoute.uniqueId) && plannedRouteMarkersData?.features?.length > 0){
      plannedRouteFocusOnMap.current = true
    }
    
    // Create polyline(s) and decorator
    const coords = plannedRoute.trail.geometry.coordinates.map(
      ([lon, lat]) => [lat, lon] as [number, number]
    );

    if (coords === undefined){
      return
    }

    setLocalSelectedPlannedRouteId(plannedRoute.uniqueId)

    const polyline = L.polyline(coords, {
      color: PLANNED_ROUTE_COLOR,
      weight: 7,
      opacity: 0.7,
    }).addTo(map);
    plannedRoutePolylineRef.current = polyline

    const decorator = (L as any).polylineDecorator(polyline, {
      patterns: [
        {
          offset: 0,
          repeat: 50,
          symbol: (L as any).Symbol.arrowHead({
            pixelSize: 7,
            polygon: false,
            pathOptions: { stroke: true, color: PLANNED_ROUTE_ARROW_COLOR, weight: 2 },
          }),
        },
      ],
    });

    decorator.addTo(map);
    plannedRouteDecoratorRef.current = decorator;

    // Move map
    if (plannedRouteFocusOnMap.current && map != null && map.getZoom() > map.getMinZoom()){
      // Enumerate points and get min/max points for bounds
      const points = plannedRouteMarkersData?.features?.map(
        (f: any) => f.geometry.coordinates
      ) || [];
      if (points.length > 0) {
        let minPoint = [...points[0]] as [number, number];
        let maxPoint = [...points[0]] as [number, number];
        points.forEach(([lng, lat]: [number, number]) => {
          if (lat < minPoint[1]) minPoint[1] = lat;
          if (lat > maxPoint[1]) maxPoint[1] = lat;
          if (lng < minPoint[0]) minPoint[0] = lng;
          if (lng > maxPoint[0]) maxPoint[0] = lng;
        });
        const bounds = L.latLngBounds(
          [minPoint[1], minPoint[0]],
          [maxPoint[1], maxPoint[0]]
        );
        map.fitBounds(bounds, { paddingTopLeft: [-mapPadding, 0], maxZoom: 17, animate: true });
      }
    }
  }, [map, plannedRouteMarkersData, trailsData, cleanupPlannedRoute, plannedRoute, plannedRoute?.trail, plannedRoute?.trail?.geometry.coordinates, localSelectedPlannedRouteId, mapPadding]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function cleanupPlannedRoute(){
    if (plannedRouteDecoratorRef.current && plannedRoutePolylineRef.current && map) {
      map.removeLayer(plannedRoutePolylineRef.current);
      map.removeLayer(plannedRouteDecoratorRef.current);
      plannedRoutePolylineRef.current = null;
      plannedRouteDecoratorRef.current = null;
      plannedRouteFocusOnMap.current = false;
    }
  }

  const { handleWhenCreatedMap, mapRef } =
    useDefaultStateForMapInstance(handleSetMap);

  const handleLayerClick = (waypointId: string) => {
    setLocalSelectedWaypointId(waypointId);
    dispatch(actions.setSelectedWaypoint(waypointId));
    dispatch(actions.setIsWaypointDetailsOpen(true));
  };

  const handleEachWaypoint = (feature: any, marker: L.Marker): void => {
    const markerIcon = L.divIcon({
      html: ReactDOMServer.renderToString(
        <MarkerIcon
          svgFilePath={
            selectedWaypoint?.id === feature.id
              ? getWaypointIcon("selected")
              : getWaypointIcon(feature?.waypointIconSet[0])
          }
          color={feature?.markerColor}
        />
      ),
      className: MarkerClass,
      iconAnchor: [15, 40],
    });

    marker.setIcon(markerIcon);

    marker.on({
      click: () => handleLayerClick(feature.id),
    });

    allMarkersRef.current.push(marker);
  };

  const handleEachTrail = (feature: any, layer: L.Path) => {
    layer.options = {
      ...layer.options,
      color: `#${feature.stroke}` || undefined,
      opacity: feature.strokeOpacity || 1,
      weight: feature.strokeWidth || 3,
    };
    if (useTrailPopup && !isEditorMode) {
        layer.bindPopup(feature.trackName);
    }
  };

  const handleEachPlannedRouteMarker = (feature: any, marker: L.Marker): void => {
    const markerIcon = L.divIcon({
      html: ReactDOMServer.renderToString(
        <MarkerIcon
          svgFilePath="/icons/circle.svg"
          color="00000000"
          width={12}
          height={12}
        />
      ),
      className: MarkerClass,
      iconAnchor: [6, 6],
    });

    marker.setIcon(markerIcon);

    // marker.on({
    //   click: () => handleLayerClick(feature.id),
    // });

    // allMarkersRef.current.push(marker);
  };

  useEffect(() => {
    const localStorage = LocalStorageManager.getInstance();
    const actualLayerLinkJSON: string | null =
      localStorage.getItem('layerData');

    if (actualLayerLinkJSON) {
      const actualLayerLink: { attribution?: string; link?: string } =
        JSON.parse(actualLayerLinkJSON);

      actualLayerLink.attribution &&
        dispatch(actions.setMapLayerAttribution(actualLayerLink.attribution));
      actualLayerLink.link &&
        dispatch(actions.setMapLayerLink(actualLayerLink.link));
    }
  }, [dispatch]);

  useEffect(() => {
      const selectedIcon = L.divIcon({
        html: ReactDOMServer.renderToString(
          <MarkerIcon svgFilePath={getWaypointIcon("selected")} />
        ),
        className: MarkerClass,
        iconAnchor: [15, 40],
      });

      allMarkersRef.current.forEach((layer: L.Marker) => {
        const markerIcon = L.divIcon({
          html: ReactDOMServer.renderToString(
            <MarkerIcon
                svgFilePath={
                    selectedWaypoint?.id === layer.feature?.id
                      ? getWaypointIcon("selected")
                      : getWaypointIcon((layer.feature as any)?.waypointIconSet[0])
                  }
            />
          ),
          className: MarkerClass,
          iconAnchor: [15, 40],
        });

        selectedWaypoint?.id === layer.feature?.id
          ? layer.setIcon(selectedIcon)
          : layer.setIcon(markerIcon);
      });
  }, [selectedWaypoint, waypointsData]);

  useEffect(() => {
    if (tileLayerRef.current) {
      tileLayerRef.current.setUrl(`${currentLayerLink}`);
    }
  }, [currentLayerLink]);

  const createClusterCustomIcon = (cluster: any) => {
    const iicon = cluster.getAllChildMarkers().map((icon: L.Marker) => {
      return (icon.options.icon?.options as L.DivIconOptions).html;
    });

    return L.divIcon({
      html:
        cluster.getAllChildMarkers().length > 0
          ? `<div>${iicon
              .slice(0, 5)
              .map((el: string, index: number) => {
                const styles = getClusterInlineStyles(index);

                return `<div class="${CustomMarkerClass}" style="${styles}">${el}</div>`;
              })
              .join('')}</div>`
          : `<div class="${CustomMarkerClass}">${cluster.getChildCount()}</div>`,
      className: `${ClusterClass}`,
      iconSize: L.point(20, 27, true),
      iconAnchor: [19, 42],
    });
  };

  const handleOutsideClick = useCallback(() => {
    dispatch(actions.setIsWaypointDetailsOpen(false));
    selectedWaypoint && dispatch(actions.setSelectedWaypoint(''));
  }, [dispatch, selectedWaypoint]);

  useEffect(() => {
    mapRef.current?.addEventListener('click', handleOutsideClick);

    return () => {
      mapRef.current?.removeEventListener('click', handleOutsideClick);
    };
  }, [mapRef, handleOutsideClick]);

  const handleFitBounds = useCallback(
    (bounds: LatLngBoundsLiteral | null) => {
      if (map) {
        bounds
          ? map.fitBounds(bounds, {
            paddingTopLeft: [-mapPadding, 0],
          })
          : map.flyTo([0, 0], 2, {
              duration: 1,
            });
      }
    },
    [map]
  );

  useEffect(() => {
    if (trailsData?.bbox && map != null) {
      if (initialOpening.current && !isWaypointSelected) {
        const geoBounds: LatLngBoundsLiteral = [
          [trailsData.bbox[0], trailsData.bbox[1]],
          [trailsData.bbox[2], trailsData.bbox[3]],
        ];
        handleFitBounds(geoBounds);
      }
      initialOpening.current = false;
    }
  }, [map, trailsData, handleFitBounds, plannedRouteFocusOnMap, isWaypointSelected]);

  useEffect(() => {
    if (
      map &&
      selectedWaypoint &&
      selectedWaypoint.id !== localSelectedWaypointId
    ) {
      const [coord1, coord2] = selectedWaypoint.geometry.coordinates;

      const pointLatLng = L.latLng(coord2, coord1);
      const bounds = L.latLngBounds([pointLatLng, pointLatLng]);

      map.fitBounds(bounds, {
        paddingTopLeft: [-mapPadding, 0], // leave space for left sidebar
        maxZoom: 17,
        animate: true
      });      
    }
    setLocalSelectedWaypointId('');
  }, [selectedWaypoint, map]);

  // 
  // const [points, setPoints] = useState<LatLngExpression[]>([]);
  const ClickHandler: React.FC<{ onClick: (latlng: L.LatLng) => void }> = ({ onClick }) => {
      useMapEvent("click", (e) => {
          onClick(e.latlng);
      });
      return null;
  };

  return (
    <S.Container isMobile={isMobile}>
      <S.MapContainer
        bounds={
          trailsData?.bbox && [
            [trailsData.bbox[0], trailsData.bbox[1]],
            [trailsData.bbox[2], trailsData.bbox[3]],
          ]
        }
        maxBoundsViscosity={1}
        maxBounds={[
          [-80, -175],
          [90, 265],
        ]}
        center={[40, 0]}
        zoom={3}
        scrollWheelZoom
        zoomControl={false}
        attributionControl={true}
        maxZoom={20}
        minZoom={3}
        whenCreated={handleWhenCreatedMap}
        preferCanvas
        renderer={L.canvas({ padding: 1 })}
      >
        <TileLayer
          attribution={currentLayerAttribution || DEFAULT_LAYER.attribution}
          url={`${currentLayerLink || DEFAULT_LAYER.link}`}
          ref={tileLayerRef}
        />
        <ScaleControl />

        {/* Trails */}
        {trailsData && (
          <GeoJSON key={isEditorMode?1:0} data={trailsData} onEachFeature={handleEachTrail} />
        )}

        {/* Planned Route */}
        {/* {plannedRoute != null && plannedRouteTrailGeoJsonData !== undefined && (
          <GeoJSON key={plannedRouteTrailGeoJsonData.key}
            data={plannedRouteTrailGeoJsonData} onEachFeature={handleEachPlannedRouteTrail} />
        )} */}

        {/* Waypoints */}
        {waypointsData && (
          <S.MarkerGroup
            iconCreateFunction={createClusterCustomIcon}
            showCoverageOnHover={false}
          >
            <GeoJSON data={waypointsData} onEachFeature={handleEachWaypoint} />
          </S.MarkerGroup>
        )}

        {/* Planned Route Markers */}
        {plannedRoute != null && plannedRouteMarkersData && plannedRouteMarkersData !== undefined && (
          <GeoJSON key={plannedRouteMarkersData?.key} 
            data={plannedRouteMarkersData} onEachFeature={handleEachPlannedRouteMarker} />
        )}

        {/* Handle short clicks */}
        <ClickHandler onClick={handleAddPlannedRoutePin} />

        {/* Markers for each click */}
{/*         {points.map((pos, idx) => (
          <Marker key={idx} position={pos} />
        ))}
 */}
      </S.MapContainer>
    </S.Container>
  );
};

export const GuideUseMapView = memo(GuideUseMapComponent);
